import CanvasPluginProvider from '@/components/canvas/CanvasPluginProvider'
import { useMeasurementStore } from '@/store/measurements'
import Konva from 'konva'
import { useCamerasStore } from '@/store/cameras'
import { CanvasPluginSettings } from '@/types/Canvas'
import { Reactive } from 'vue'

type LongPosition = {
  time: string
  height: number
  score: number
  width: number
  xCenter: number
  yCenter: number
  camera: string
  label: string
}

type ConfigType = {
  enabled: boolean
}

type FrequencyUnit = 'second' | 'minute' | 'hour'

type UpdateFrequency = {
  every: number
  unit: FrequencyUnit
}

type LabelConfig = {
  color: string
  frequency: UpdateFrequency
}

const LABEL_CONFIG: Record<string, LabelConfig> = {
  human: { color: 'cyan', frequency: { every: 1, unit: 'second' } },
  'feeding-container': { color: 'green', frequency: { every: 1, unit: 'minute' } },
  'drinking-nipple': { color: 'blue', frequency: { every: 1, unit: 'minute' } },
  'elevated-platform': { color: 'yellow', frequency: { every: 1, unit: 'minute' } },
  not_blood: { color: 'green', frequency: { every: 1, unit: 'minute' } },
  blood: { color: 'red', frequency: { every: 1, unit: 'minute' } },
  'wet-spot': { color: 'orange', frequency: { every: 1, unit: 'minute' } },
  'spilled-feed': { color: 'black', frequency: { every: 1, unit: 'minute' } },
}

function frequencyKey(f: UpdateFrequency): string {
  return `${f.every}_${f.unit}`
}

function alignTimestamp(timestampMs: number, frequency: UpdateFrequency): number {
  const unitMs: Record<FrequencyUnit, number> = {
    second: 1000,
    minute: 1000 * 60,
    hour: 1000 * 60 * 60,
  }
  const intervalMs = frequency.every * unitMs[frequency.unit]
  return Math.floor(timestampMs / intervalMs) * intervalMs
}

export class LongposPlugin extends CanvasPluginProvider<ConfigType> {
  reactiveProperties: Reactive<{ enabled: boolean; loading: boolean; positions: LongPosition[] }>

  constructor(settings: CanvasPluginSettings, config: ConfigType) {
    super(settings, 'longpos', config, true, true)
    this.reactiveProperties = { loading: false, enabled: settings.enabled, positions: [] }
  }

  fetchInformation(): [Promise<void>, AbortController] {
    this.reactiveProperties.positions = []
    this.draw()
    const controller = new AbortController()

    const timestampMs = this.settings.timestamp.toMillis()

    const groups = Object.entries(LABEL_CONFIG).reduce<Record<string, { frequency: UpdateFrequency; labels: string[] }>>(
      (acc, [label, { frequency }]) => {
        const key = frequencyKey(frequency)
        if (!acc[key]) acc[key] = { frequency, labels: [] }
        acc[key].labels.push(label)
        return acc
      },
      {}
    )

    const queries: Promise<LongPosition[]>[] = Object.values(groups).map(({ frequency, labels }) => {
      const alignedStart = alignTimestamp(timestampMs, frequency)
      return useMeasurementStore().loadGenericMeasurement(
        'longPosition',
        {
          groupByTags: true,
          time: { start: alignedStart, end: alignedStart + 1 },
          where: { label: labels },
        },
        controller.signal
      )
    })

    const promise = new Promise<void>((resolve, reject) => {
      Promise.all(queries)
        .then(results => {
          this.reactiveProperties.positions = results.flat()
          resolve()
        })
        .catch(reject)
    })

    return [promise, controller]
  }

  draw() {
    this.group.destroyChildren()
    if (!this.reactiveProperties.positions?.length) return

    for (const position of this.reactiveProperties.positions) {
      const cameraId = position.camera
      const elementPosition = this.settings.elementPositions[cameraId]
      if (!elementPosition) continue

      const camera = useCamerasStore().getCameraById(undefined, cameraId)
      if (!camera) continue

      const x = (position.xCenter - position.width / 2) * elementPosition.width
      const y = (position.yCenter - position.height / 2) * elementPosition.height
      const w = position.width * elementPosition.width
      const h = position.height * elementPosition.height

      const transform = new Konva.Transform()
      transform.translate(elementPosition.x, elementPosition.y)
      transform.rotate(elementPosition.rotation * (Math.PI / 180))

      const color = LABEL_CONFIG[position.label]?.color ?? 'red'
      const box = new Konva.Rect({
        x: transform.point({ x, y }).x,
        y: transform.point({ x, y }).y,
        width: w,
        height: h,
        strokeWidth: 2,
        stroke: color,
        strokeScaleEnabled: false,
        rotation: elementPosition.rotation,
      })

      box.on('mouseenter', ev => {
        this.settings.tooltipEnable({
          x: ev.evt.clientX + 2,
          y: ev.evt.clientY + 2,
          text: `${position.label} (${(position.score * 100).toFixed(2)}%)`,
        })
      })

      box.on('mousemove', ev => {
        this.settings.tooltipEnable({
          x: ev.evt.clientX + 2,
          y: ev.evt.clientY + 2,
          text: `${position.label} (${(position.score * 100).toFixed(2)}%)`,
        })
      })

      box.on('mouseleave', () => {
        this.settings.tooltipDisable()
      })

      box.preventDefault(false)
      this.group.add(box)
    }
  }

  doesConfigChangeNeedRerender(): boolean {
    return false
  }

  doesConfigChangeNeedReload(): boolean {
    return false
  }
}
